import { journey } from '../lib/journey';
import { racePresentation } from '../data/racePresentation';
import '../styles/race.css';
import { PracticeGame } from '../components/PracticeGame';
import { RunnerDock } from '../components/RunnerDock';
import { betweenRaces } from '../data/demo/PracticeWorld';
import { useSteeringPlay } from '../components/useSteeringPlay';
import { SteeringPanel } from '../components/SteeringPanel';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { LaunchBar } from '../components/LaunchCard';
import { useLaunchInfo } from '../data/launch';
import { IconArrowLeft, IconArrowRight, IconPause, IconPlay, IconOperator, IconJournal, Mascot } from '@runner/ui';
import { useWorld } from '../data/WorldProvider';
import { WorldCanvas, type WorldCanvasHandle } from '../render/WorldCanvas';
import { usePrefs, SkinPicker } from '../app/prefs';
import {
  BaseRewardsPanel,
  countdown,
  Feed,
  NextChip,
  steerState,
  NextEncounters,
  PhaseExplainer,
  PhasePill,
  PressureMeter,
  RunDecidesPanel,
  RunPins,
} from '../components/game';
import { EquipControl, ResultCard } from '../components/runner';
import { RaceChip } from '../components/RaceChip';
import { RunDayPanel, useRunDay } from '../components/RunDay';
import { TradeDrawer } from '../components/TradeDrawer';
import { More } from '../components/More';
import { Link } from '../app/router';
import { big } from '../data/model';
import { units, secs } from '../lib/format';
import type { WorldView } from '../data/types';

function whereText(v: WorldView): string {
  if (!v.run || v.run.status === 'awaiting-randomness') return v.phase === 'funding' ? 'RUNNER waits at the start line while the course is drawn.' : 'RUNNER is warming up at the start line.';
  const last = v.steps[v.steps.length - 1];
  const y = last ? Number(last.state.y) : 0;
  const x = last ? Number(last.state.x) : 0;
  const side = x > 80 ? `${Math.round((x / 1000) * 100)}% right of center` : x < -80 ? `${Math.round((-x / 1000) * 100)}% left of center` : 'near the center line';
  const len = v.run.courseLength;
  // The last step can carry RUNNER past the line; the course ends there, so never print a position beyond it.
  const along = len > 0 && y >= len ? `finish line crossed (${len.toLocaleString()} units)` : `${y.toLocaleString()} of ${len.toLocaleString()} units`;
  return `Step ${v.steps.length} of up to ${v.run.maxSteps} · ${along} · ${side}.`;
}

type Tab = 'next' | 'money' | 'activity' | 'earn' | 'runner';

/** Trading controls retain the road's left/right meaning. */
function Wing({ side, lead, live, onPress }: { side: 'buy' | 'sell'; lead: boolean; live: boolean; onPress(): void }) {
  const buy = side === 'buy';
  return (
    <button className={`wing ${side}${lead ? ' lead' : ''}`} onClick={onPress}>
      <span className="wing-arrow" aria-hidden="true">
        {buy ? <IconArrowRight size={20} /> : <IconArrowLeft size={20} />}
      </span>
      <span className="wing-copy">
        <span className="wing-word">{buy ? 'Buy' : 'Sell'}</span>
        <span className="wing-direction">{live ? `Push ${buy ? 'right' : 'left'}` : 'RUNNER'}</span>
      </span>
      <kbd className="hide-touch" aria-hidden="true">{buy ? 'D' : 'A'}</kbd>
    </button>
  );
}

/** One way to earn: the whole card is the link. */
function EarnCard({ art, title, children, to, testId }: { art: ReactNode; title: string; children: ReactNode; to: string; testId?: string }) {
  return (
    <Link to={to} className="earn-card" data-testid={testId}>
      <span className="earn-art">{art}</span>
      <span>
        <strong>{title} →</strong>
        <span className="small">{children}</span>
      </span>
    </Link>
  );
}

export function RunPage() {
  const { view, source } = useWorld();
  const prefs = usePrefs();
  const [watchRace, setWatchRace] = useState(false);
  const practice = betweenRaces(view) && !watchRace;
  const [drawer, setDrawer] = useState<null | 'buy' | 'sell'>(null);
  // Directional controls size against current input; amounts remain capped by the player’s limits.
  const play = useSteeringPlay(view, source);
  const canvas = useRef<WorldCanvasHandle>(null);

  // Keyboard opens the same review form as Buy/Sell; it never sends a trade.
  useEffect(() => {
    if (practice) return;
    const on = (e: KeyboardEvent) => {
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey || document.querySelector('[role="dialog"]')) return;
      if ((e.target as HTMLElement)?.closest('input, textarea, select, [contenteditable="true"]')) return;
      const k = e.key.toLowerCase();
      const side = k === 'a' || k === 'arrowleft' ? 'sell' : k === 'd' || k === 'arrowright' ? 'buy' : null;
      if (side) { e.preventDefault(); setDrawer(side); }
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [practice]);
  const shoe = view.run?.shoe;
  const left = countdown(view);
  const finished = view.run?.status === 'finished';
  const tug = steerState(view);
  const q = view.quote;
  const B = view.collection ? big(view.collection.backing) : 0n;
  const owned = !!view.runners?.owned.length;
  const [tab, setTab] = useState<Tab | null>(null);
  const infoTrigger = useRef<HTMLButtonElement>(null);
  const infoPanel = useRef<HTMLElement>(null);
  const infoOpen = tab !== null;
  useEffect(() => { if (infoOpen) infoPanel.current?.querySelector<HTMLButtonElement>('[aria-selected="true"]')?.focus({ preventScroll: true }); }, [infoOpen]);
  const closeInfo = () => { setTab(null); infoTrigger.current?.focus({ preventScroll: true }); };
  const tabs: [Tab, string][] = [['next', 'Next'], ['money', 'Money'], ['activity', 'Activity'], ['earn', 'Earn'], ...(owned ? [['runner', 'My Runner']] as [Tab, string][] : [])];
  const races = useLaunchInfo()?.races;
  const runDay = useRunDay();
  const state = racePresentation(view);
  useEffect(() => { journey('race_viewed', { phase: state.kind, mode: view.mode }); }, [state.kind, view.mode]);
  useEffect(() => { if (finished && !practice) journey('result_viewed', { mode: view.mode }); }, [view.run?.id, finished, practice]);
  const openTrade = (side: 'buy' | 'sell') => { play.close(); setDrawer(side); };
  return (
    <div className="page play race-page" data-race-state={state.kind}>
      <LaunchBar />
      {runDay && <RunDayPanel view={view} event={runDay} />}
      <header className="race-intro">
        <div><span className={`race-state-label ${state.kind}`} data-testid="hud-status">{state.label}{view.run ? ` · Run ${view.run.id}` : ''}</span><h1 id="world-h">{state.title}</h1><p>{state.description}</p></div>
        <RaceChip view={view} />
      </header>
      {practice ? <PracticeGame><div className="practice-next"><span>The live race appears here when it starts.</span><button className="btn small" onClick={() => setWatchRace(true)}>{view.run?.status === 'finished' ? `View race ${view.run.id} result` : 'View live race status'}</button><a href="/demo">Share the demo ↗</a></div></PracticeGame> : <section className="race-arena" aria-labelledby="world-h">
        <div className="race-course">
          <WorldCanvas view={view} className="world-canvas" ref={canvas} ambient={!prefs.labels} steeringPreview={state.canInfluence ? play.plan : null} onAim={state.canInfluence && play.aim ? (x) => play.select({ kind: 'point', x }) : undefined} />
          {state.kind === 'offline' && <div className="race-connection-note">Live course paused · <a href="/demo">Play free practice ↗</a></div>}
          {(state.kind === 'live' || state.kind === 'starting') && <div className="race-next"><NextChip view={view} /></div>}
          {state.canInfluence && <SteeringPanel play={play} view={view} onManual={() => openTrade(play.aim?.kind === 'left' ? 'sell' : 'buy')} />}
        </div>
        <div className="race-toolbar">
          <div className="race-pressure" data-testid="steer-meter" data-steering={state.canInfluence ? 'yes' : 'no'}>
            <span>{state.canInfluence ? 'Market pressure' : state.label}</span>
            {state.canInfluence && <><div className="tug-bar" role="meter" aria-valuemin={-tug.cap} aria-valuemax={tug.cap} aria-valuenow={tug.u} aria-label="Net steering this window"><div className={`tug-fill ${tug.u < 0 ? 'left' : 'right'}`} style={{ width: `${tug.share}%` }} /><div className="tug-mid" /></div><small>{tug.u === 0 ? 'Balanced' : tug.u > 0 ? 'Buys lead →' : '← Sells lead'}{left !== null ? ` · ${secs(left)} left` : ''}</small></>}
          </div>
          <div className="race-display-controls" role="group" aria-label="Animation controls">
            <button className="icon-btn" aria-pressed={prefs.paused} onClick={() => prefs.setPaused(!prefs.paused)} aria-label={prefs.paused ? 'Resume animation' : 'Pause animation'}>{prefs.paused ? <IconPlay /> : <IconPause />}</button>
            <details className="display-settings" onKeyDown={(e) => { if (e.key === 'Escape') { e.currentTarget.open = false; e.currentTarget.querySelector('summary')?.focus(); } }}>
              <summary className="icon-btn" aria-label="Display settings"><IconOperator size={20} /></summary>
              <div className="display-settings-panel"><span className="small muted">Display settings</span><button className="setting-toggle" aria-pressed={prefs.labels} onClick={() => prefs.setLabels(!prefs.labels)} aria-label="Toggle object labels">Object labels <span>{prefs.labels ? 'On' : 'Off'}</span></button><button className="setting-toggle" aria-pressed={prefs.reducedMotion} onClick={() => prefs.setReducedMotion(!prefs.reducedMotion)} aria-label="Toggle reduced motion">Reduced motion <span>{prefs.reducedMotion ? 'On' : 'Off'}</span></button></div>
            </details>
            <SkinPicker />
            {finished && <button className="btn small ghost" data-testid="replay-run" onClick={() => canvas.current?.replay()}>Replay run {view.run!.id}</button>}
          </div>
        </div>
      </section>}
      <div className="race-actions" role="group" aria-label="Trade RUNNER">
        <Wing side="sell" lead={state.canInfluence && tug.u < 0} live={state.canInfluence} onPress={() => openTrade('sell')} />
        <div className="race-action-note"><strong>{state.canInfluence ? 'Your trade adds to the market’s move.' : 'Trade RUNNER any time.'}</strong><span>{state.canInfluence ? 'Other trades can change the final path.' : 'Free practice never spends your tokens.'}</span>{state.canInfluence && <button className="race-influence" onClick={() => play.select({ kind: 'right' })}>Preview influence ↗</button>}</div>
        <Wing side="buy" lead={state.canInfluence && tug.u > 0} live={state.canInfluence} onPress={() => openTrade('buy')} />
      </div>
      <div className="race-links"><RunnerDock /><div>{betweenRaces(view) && !practice ? <button className="btn small ghost" onClick={() => setWatchRace(false)}>Play free practice</button> : <a href="/demo">Free demo ↗</a>}<button ref={infoTrigger} className="btn small ghost" aria-expanded={infoOpen} aria-controls="run-information" onClick={() => infoOpen ? closeInfo() : setTab('next')}><IconJournal size={16} /> Race details</button></div></div>
      {finished && !practice && <ResultCard view={view} />}
        <aside ref={infoPanel} id="run-information" className="race-details" aria-label="Run information" hidden={!infoOpen} onKeyDown={(e) => {
          if (e.key === 'Escape') { e.stopPropagation(); closeInfo(); }
        }}>
          <div className="hud-tabs" role="tablist" aria-label="Run information">
            {tabs.map(([id, label]) => (
              <button key={id} role="tab" id={`tab-${id}`} aria-selected={tab === id} aria-controls={`pane-${id}`} onClick={() => setTab(id)}>
                {label}
              </button>
            ))}
            {tab && (
              <button className="hud-close" onClick={closeInfo} aria-label="Hide run information">
                ×
              </button>
            )}
          </div>
          <div className="hud-panes">
            <div role="tabpanel" id="pane-next" aria-labelledby="tab-next" hidden={tab !== 'next'} className="stack">
              {shoe && (
                <div className="hud-shoe">
                  <strong>Golden Shoe · {units(shoe.amount, view.quote.decimals)} {view.quote.symbol}</strong>
                  <span className="small">
                    {shoe.winnerLabel
                      ? `Won by ${shoe.winnerLabel}.`
                      : view.timing.now >= shoe.cutoff
                        ? 'Entries closed — RUNNER has to grab it.'
                        : 'Buy before entries close to be in the draw if RUNNER grabs it.'}
                  </span>
                  <More label="Entries and your share">
                    <dl className="kv">
                      <dt>Status</dt>
                      <dd data-testid="shoe-status">{shoe.status}</dd>
                      <dt>Entries</dt>
                      <dd>{shoe.entries}</dd>
                      <dt>Your share of entries</dt>
                      <dd data-testid="shoe-share">{shoe.totalWeight > 0n ? `${((Number(shoe.yourWeight) / Number(shoe.totalWeight)) * 100).toFixed(1)}%` : '—'}</dd>
                      <dt>Entries close</dt>
                      <dd>{view.timing.now >= shoe.cutoff ? 'closed' : view.freshness.stale ? 'unknown (data stale)' : `in ${secs(shoe.cutoff - view.timing.now)}`}</dd>
                    </dl>
                    <p>Not a win chance: it can change before entries close, and RUNNER must actually grab the shoe.</p>
                  </More>
                </div>
              )}
              <div>
                <h2>Coming up</h2>
                {!view.run && races?.times.length ? (
                  <p className="small muted" style={{ margin: 0 }}>
                    The course appears when the next race is prepared. Explore rewards and your Runners while you wait.
                  </p>
                ) : <NextEncounters view={view} />}
              </div>
              <More label="About this run">
                <div className="stack" style={{ gap: 8 }} data-testid="phase-panel">
                  <div>
                    <PhasePill view={view} />
                    {view.run?.abandoned && <span className="tag blocked">abandoned — budget returned</span>}
                  </div>
                  <PhaseExplainer view={view} />
                  <p style={{ margin: 0, fontWeight: 700 }} data-testid="where">
                    {whereText(view)}
                  </p>
                  {(view.phase === 'running' || view.phase === 'preview') && <PressureMeter view={view} />}
                  <RunPins view={view} />
                </div>
              </More>
            </div>
            <div role="tabpanel" id="pane-money" aria-labelledby="tab-money" hidden={tab !== 'money'} className="stack">
              <RunDecidesPanel view={view} />
              <BaseRewardsPanel view={view} />
            </div>
            <div role="tabpanel" id="pane-activity" aria-labelledby="tab-activity" hidden={tab !== 'activity'}>
              <h2>What just happened</h2>
              <Feed items={view.feed} max={12} />
            </div>
            <div role="tabpanel" id="pane-earn" aria-labelledby="tab-earn" hidden={tab !== 'earn'} className="stack">
              <h2 id="earn-h">Ways to earn</h2>
              <EarnCard art={<Mascot pose="celebrate" size={56} title="" />} title="Win the Golden Shoe" to="/trade">
                Buy through RUNNER before its entry cutoff{shoe ? ` (${units(shoe.amount, q.decimals)} ${q.symbol} now)` : ''}. If RUNNER grabs it, one of those buyers wins.
              </EarnCard>
              <EarnCard art={<Mascot pose="cool" size={56} title="" />} title="Earn from trading fees" to="/rewards" testId="home-rewards">
                Deposit RUNNER and earn when I reach a water station or finish on the Harvest side — no rate, no minimum, no promise.
              </EarnCard>
              <EarnCard art={<Mascot pose="finish" size={56} title="" />} title="Collect a Runner" to="/runners" testId="home-runners">
                Optional, backed by {B > 0n ? units(B, 18, 0) : 'a fixed amount of'} RUNNER of your deposit. You never need one to play.
              </EarnCard>
              <p className="small muted" style={{ margin: 0 }}>
                No promised returns. <Link to="/hooks">Every rule in detail →</Link>
              </p>
            </div>
            {owned && (
              <div role="tabpanel" id="pane-runner" aria-labelledby="tab-runner" hidden={tab !== 'runner'}>
                <h2>My Runner</h2>
                <EquipControl view={view} />
              </div>
            )}
          </div>
        </aside>
      {drawer && <TradeDrawer side={drawer} onClose={() => setDrawer(null)} />}
    </div>
  );
}
